import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  italianToday,
  type JobIndicatorsResponse,
  type JobResponse,
  type Light,
  type JobRisk,
  type ProgressRequest,
  progressProblem,
  progressSchema,
  RISK_AREA_LABELS,
  RISK_AREAS,
  RISK_KIND_LABELS,
  RISK_KINDS,
  RISK_STATE_LABELS,
  RISK_STATES,
  type RiskRequest,
  type RiskSeverity,
  riskSchema,
} from '@k-management/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { Indicators } from './Indicators.js';
import { jobKeys } from './jobs-ui.js';

export const indicatorsKey = (id: string) => [...jobKeys.all, 'indicators', id] as const;

const LIGHT_CLASS: Record<Light, string> = { verde: 'green', giallo: 'yellow', rosso: 'red' };
const SEVERITY_CLASS: Record<RiskSeverity, string> = {
  bassa: 'green',
  media: 'yellow',
  alta: 'red',
};

/** «40 %», «0,89»: Italian numbers, null as a dash. */
function number(value: number | null, suffix = '', digits = 1): string {
  if (value === null) return '—';
  return `${value.toLocaleString('it-IT', { maximumFractionDigits: digits })}${suffix}`;
}

function money(value: string | null): string {
  return value === null ? '—' : formatMoney(value);
}

export function LightDot({ light }: { light: Light | null }) {
  if (!light) return null;
  return (
    <span className={`margin-light ${LIGHT_CLASS[light]}`} title={`Semaforo ${light}`}>
      <span className="dot" />
      {light}
    </span>
  );
}

function Tile({
  label,
  value,
  light,
  note,
}: {
  label: string;
  value: string;
  light: Light | null;
  note?: ReactNode;
}) {
  return (
    <div className={`card kpi indicator-tile${light ? ` ${LIGHT_CLASS[light]}` : ''}`}>
      <div className="row between">
        <small className="muted">{label}</small>
        <LightDot light={light} />
      </div>
      <strong>{value}</strong>
      {note && <small className="muted">{note}</small>}
    </div>
  );
}

/**
 * Gli indicatori di una commessa o di un progetto (2.45, ADR-027): the
 * avanzamento from the SAL or the hours, SPI, and for who sees the costs CPI,
 * cost to finish, margins and the accrued not invoiced. Semafori from the table
 * «Soglie degli indicatori».
 */
export function JobIndicatorsView({ job }: { job: JobResponse }) {
  const [updating, setUpdating] = useState(false);
  const [risk, setRisk] = useState<JobRisk | 'new' | null>(null);
  const query = useQuery({
    queryKey: indicatorsKey(job.id),
    queryFn: () => apiRequest<JobIndicatorsResponse>(`/jobs/${job.id}/indicators`),
  });
  if (query.isError) return <p className="error">{query.error.message}</p>;
  if (!query.data) return <p className="muted">Caricamento…</p>;
  const { indicators, history, canUpdate, risks, risksSummary } = query.data;
  const { progress, hours, spi, economics } = indicators;
  const commessa = job.kind === 'commessa';
  // No cost consuntivato yet: no estimate of the cost to finish nor of the margin now.
  const nothingSpent = economics !== null && Number(economics.ac) === 0;

  return (
    <div className="stack">
      <div className="card stack">
        <div className="row between">
          <div className="row">
            <strong>Avanzamento {number(progress.percent, ' %')}</strong>
            {progress.source === 'sal' && progress.salOn && (
              <span className="pill">dichiarato dal PM il {formatDate(progress.salOn)}</span>
            )}
            {progress.source === 'ore' && <span className="pill">dalle ore: nessun SAL</span>}
            {progress.source === 'sal' && progress.fromHours !== null && (
              <span className="muted small">
                · dalle ore sarebbe {number(progress.fromHours, ' %')}
              </span>
            )}
          </div>
          {canUpdate && (
            <button type="button" className="primary" onClick={() => setUpdating(true)}>
              Aggiorna l’avanzamento (SAL)…
            </button>
          )}
        </div>
        {progress.percent === null && (
          <p className="muted">
            Nessun SAL e nessuna ora prevista: l’avanzamento non si può calcolare. Lo dichiara il PM
            con «Aggiorna l’avanzamento», oppure si ricava dalle ore in «Persone e ore».
          </p>
        )}
        {hours.light && hours.light !== 'verde' && hours.gap !== null && hours.gap > 0 && (
          <p className="notice info">
            Le ore lavorate ({number(progress.fromHours, ' %')} delle previste) corrono più
            dell’avanzamento ({number(progress.percent, ' %')}): verifica la stima o il consumo di
            ore.
          </p>
        )}
      </div>

      <div className="kpi-row">
        <Tile
          label="SPI · tempi"
          value={number(spi.value, '', 2)}
          light={spi.light}
          note={
            economics
              ? `EV ${money(economics.ev)} / PV ${money(economics.pv)}`
              : 'valore maturato / pianificato a oggi'
          }
        />
        {economics && (
          <>
            <Tile
              label="CPI · costi"
              value={number(economics.cpi.value, '', 2)}
              light={economics.cpi.light}
              note={`EV ${money(economics.ev)} / AC ${money(economics.ac)}`}
            />
            <Tile
              label="Costo a finire (EAC)"
              value={money(economics.eac.amount)}
              light={economics.eac.light}
              note={
                nothingSpent
                  ? `budget costi ${money(economics.bac)} · nessun costo consuntivato finora`
                  : `budget costi ${money(economics.bac)}${economics.eac.overrun !== null ? ` · ${economics.eac.overrun > 0 ? '+' : ''}${number(economics.eac.overrun, ' %')}` : ''}`
              }
            />
            {commessa && (
              <>
                <Tile
                  label="Margine a finire"
                  value={number(economics.marginAtEnd.percent, ' %')}
                  light={economics.marginAtEnd.light}
                  note={`previsto ${number(economics.plannedMargin, ' %')}`}
                />
                <Tile
                  label="Margine attuale"
                  value={number(economics.marginNow.percent, ' %')}
                  light={economics.marginNow.light}
                  note={
                    nothingSpent
                      ? 'nessun costo consuntivato finora'
                      : `maturato ${money(economics.marginNow.accrued)} − speso ${money(economics.ac)}`
                  }
                />
                <Tile
                  label="Maturato non fatturato"
                  value={money(economics.unbilled.amount)}
                  light={economics.unbilled.light}
                  note={`fatturato ${money(economics.unbilled.invoiced)}`}
                />
                {economics.expenses.light && (
                  <Tile
                    label="Spese"
                    value={`${money(economics.expenses.passive)} su ${money(economics.expenses.active)}`}
                    light={economics.expenses.light}
                    note="costi spesa sull’attivo spesa: fuori dal margine"
                  />
                )}
              </>
            )}
          </>
        )}
        <Tile
          label="Ore"
          value={`${number(Number(hours.worked))} / ${number(Number(hours.planned))}`}
          light={hours.light}
          note={
            hours.gap === null
              ? 'lavorate / previste'
              : `${number(progress.fromHours, ' %')} delle ore con il ${number(progress.percent, ' %')} del lavoro`
          }
        />
        <Tile
          label="Ultimo SAL"
          value={
            progress.salDays === null
              ? 'mai'
              : progress.salDays === 0
                ? 'oggi'
                : progress.salDays === 1
                  ? '1 giorno fa'
                  : `${progress.salDays} giorni fa`
          }
          light={progress.salLight}
        />
        <Tile
          label="Rischi e problemi aperti"
          value={String(risksSummary.open)}
          light={risksSummary.light}
          note={
            risksSummary.open === 0
              ? 'nessuno'
              : `${risksSummary.grave} gravi · ${risksSummary.overdue} scaduti`
          }
        />
      </div>
      {!economics && (
        <p className="muted small">
          Costi, margini e maturato li vede solo chi ha la funzione «Costi delle persone».
        </p>
      )}

      <details className="card">
        <summary>
          <strong>Come si calcola</strong>
        </summary>
        <table className="lines">
          <tbody>
            <tr>
              <td>Avanzamento</td>
              <td>% dichiarata dal PM all’ultimo SAL; senza SAL, ore lavorate / ore previste</td>
            </tr>
            <tr>
              <td>BAC (budget costi)</td>
              <td>costi esterni previsti + ore previste × €/ora</td>
            </tr>
            <tr>
              <td>PV (pianificato a oggi)</td>
              <td>
                costi esterni con il ricevimento fino a oggi + costo delle persone in proporzione ai
                giorni tra inizio e fine
              </td>
            </tr>
            <tr>
              <td>AC (speso)</td>
              <td>fatture ricevute imputate + ore lavorate × €/ora</td>
            </tr>
            <tr>
              <td>EV (valore maturato)</td>
              <td>avanzamento × BAC</td>
            </tr>
            <tr>
              <td>SPI · CPI</td>
              <td>EV / PV · EV / AC</td>
            </tr>
            <tr>
              <td>Costo a finire (EAC)</td>
              <td>AC + (BAC − EV) / CPI</td>
            </tr>
            {commessa && (
              <>
                <tr>
                  <td>Ricavi</td>
                  <td>offerte accettate; senza offerte, le rate pianificate</td>
                </tr>
                <tr>
                  <td>Margine previsto · a finire</td>
                  <td>(ricavi − BAC) / ricavi · (ricavi − EAC) / ricavi</td>
                </tr>
                <tr>
                  <td>Maturato · margine attuale</td>
                  <td>avanzamento × ricavi · (maturato − AC) / maturato</td>
                </tr>
                <tr>
                  <td>Maturato non fatturato</td>
                  <td>maturato − fatturato</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        <p className="muted small">
          I colori seguono la tabella «Soglie degli indicatori» (Tabelle › Operations).
        </p>
      </details>

      <div className="card stack">
        <div className="row between">
          <h3>
            Rischi e problemi <span className="count-pill">{risksSummary.open}</span>
          </h3>
          {canUpdate && (
            <button type="button" onClick={() => setRisk('new')}>
              + Nuovo
            </button>
          )}
        </div>
        {risks.length === 0 ? (
          <p className="muted">
            Nessuno. Rischio: può succedere; problema: è successo; non conformità: un difetto di
            qualità.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Descrizione</th>
                  <th>Area</th>
                  <th>Gravità</th>
                  <th>Responsabile</th>
                  <th>Azione</th>
                  <th>Entro</th>
                  <th>Stato</th>
                  {canUpdate && <th />}
                </tr>
              </thead>
              <tbody>
                {risks.map((row) => (
                  <tr key={row.id} className={row.state === 'chiuso' ? 'closed' : undefined}>
                    <td>{RISK_KIND_LABELS[row.kind]}</td>
                    <td>{row.description}</td>
                    <td>{RISK_AREA_LABELS[row.area]}</td>
                    <td>
                      {row.state === 'chiuso' ? (
                        `${row.severity} · ${row.probability}×${row.impact}`
                      ) : (
                        <span className={`margin-light ${SEVERITY_CLASS[row.severity]}`}>
                          <span className="dot" />
                          {row.severity} · {row.probability}×{row.impact}
                        </span>
                      )}
                    </td>
                    <td>{row.owner ?? '—'}</td>
                    <td>{row.action ?? '—'}</td>
                    <td>
                      {row.dueOn ? formatDate(row.dueOn) : '—'}
                      {row.overdue && (
                        <>
                          {' '}
                          <span className="pill bad">scaduto</span>
                        </>
                      )}
                    </td>
                    <td>
                      {RISK_STATE_LABELS[row.state]}
                      {row.closedOn && (
                        <small className="muted"> il {formatDate(row.closedOn)}</small>
                      )}
                    </td>
                    {canUpdate && (
                      <td>
                        <button type="button" className="link" onClick={() => setRisk(row)}>
                          Modifica
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          Gravità = probabilità × impatto (da 1 a 3): alta da 6, media da 3. Rosso con qualcosa di
          aperto oltre la data, giallo con un grave aperto. I chiusi restano nel registro.
        </p>
      </div>

      {commessa && <Indicators job={job} />}

      <div className="card stack">
        <h3>Storico dei SAL</h3>
        {history.length === 0 ? (
          <p className="muted">Nessun SAL finora.</p>
        ) : (
          <table className="lines">
            <thead>
              <tr>
                <th>Data</th>
                <th className="right">Avanzamento</th>
                <th className="right">Dalle ore</th>
                <th>Chi</th>
                <th>Nota</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.on)}</td>
                  <td className="right">{number(row.percent, ' %')}</td>
                  <td className="right">{number(row.fromHours, ' %')}</td>
                  <td>{row.createdBy ?? '—'}</td>
                  <td>{row.note ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {risk && (
        <RiskDialog job={job} risk={risk === 'new' ? null : risk} onClose={() => setRisk(null)} />
      )}
      {updating && (
        <ProgressDialog
          job={job}
          previous={history[0] ?? null}
          fromHours={progress.fromHours}
          onClose={() => setUpdating(false)}
        />
      )}
    </div>
  );
}

function ProgressDialog({
  job,
  previous,
  fromHours,
  onClose,
}: {
  job: JobResponse;
  previous: { on: string; percent: number } | null;
  fromHours: number | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [on, setOn] = useState(italianToday());
  const [percent, setPercent] = useState(previous ? String(previous.percent) : '');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const save = useMutation({
    mutationFn: (body: ProgressRequest) =>
      apiRequest<JobIndicatorsResponse>(`/jobs/${job.id}/progress`, { method: 'POST', body }),
    onSuccess: async (fresh) => {
      queryClient.setQueryData(indicatorsKey(job.id), fresh);
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const submit = () => {
    const { data, errors: found } = validate(progressSchema, {
      on,
      percent: percent.trim() === '' ? undefined : Number(percent.replace(',', '.')),
      note,
    });
    setErrors(found);
    if (!data) return;
    const problem = progressProblem(data, previous, italianToday());
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError('');
    save.mutate(data);
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Nuovo stato di avanzamento</h2>
        <div className="form-grid">
          <Field label="Data" error={errors.on}>
            <input
              type="date"
              value={on}
              max={italianToday()}
              onChange={(e) => setOn(e.target.value)}
            />
          </Field>
          <Field
            label="Avanzamento %"
            error={errors.percent}
            hint={fromHours !== null ? `Dalle ore sarebbe ${number(fromHours, ' %')}` : undefined}
          >
            <input
              inputMode="decimal"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Nota"
          error={errors.note}
          hint={
            previous
              ? `Ultimo SAL: ${number(previous.percent, ' %')} il ${formatDate(previous.on)}; se scende serve la nota.`
              : undefined
          }
        >
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="button" className="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** A rischio, problema or non conformità, new or changed; closing it is a change of state. */
function RiskDialog({
  job,
  risk,
  onClose,
}: {
  job: JobResponse;
  risk: JobRisk | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [form, setForm] = useState({
    kind: risk?.kind ?? 'rischio',
    description: risk?.description ?? '',
    area: risk?.area ?? 'tempi',
    probability: String(risk?.probability ?? 2),
    impact: String(risk?.impact ?? 2),
    owner: risk?.owner ?? '',
    action: risk?.action ?? '',
    dueOn: risk?.dueOn ?? '',
    state: risk?.state ?? 'aperto',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const set = (change: Partial<typeof form>) => setForm({ ...form, ...change });
  const save = useMutation({
    mutationFn: (body: RiskRequest) =>
      apiRequest<JobIndicatorsResponse>(
        risk ? `/jobs/${job.id}/risks/${risk.id}` : `/jobs/${job.id}/risks`,
        { method: risk ? 'PUT' : 'POST', body },
      ),
    onSuccess: async (fresh) => {
      queryClient.setQueryData(indicatorsKey(job.id), fresh);
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });
  const submit = () => {
    const { data, errors: found } = validate(riskSchema, {
      ...form,
      probability: Number(form.probability),
      impact: Number(form.impact),
      dueOn: form.dueOn || null,
    });
    setErrors(found);
    if (data) save.mutate(data);
  };
  const levels = [
    ['1', '1 · bassa'],
    ['2', '2 · media'],
    ['3', '3 · alta'],
  ];

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>{risk ? 'Rischio o problema' : 'Nuovo rischio o problema'}</h2>
        <div className="form-grid">
          <Field label="Tipo" error={errors.kind}>
            <select
              value={form.kind}
              onChange={(e) => set({ kind: e.target.value as typeof form.kind })}
            >
              {RISK_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {RISK_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Area" error={errors.area}>
            <select
              value={form.area}
              onChange={(e) => set({ area: e.target.value as typeof form.area })}
            >
              {RISK_AREAS.map((area) => (
                <option key={area} value={area}>
                  {RISK_AREA_LABELS[area]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Descrizione" error={errors.description}>
          <input value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="form-grid">
          <Field label="Probabilità" error={errors.probability}>
            <select value={form.probability} onChange={(e) => set({ probability: e.target.value })}>
              {levels.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Impatto" error={errors.impact}>
            <select value={form.impact} onChange={(e) => set({ impact: e.target.value })}>
              {levels.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Responsabile" error={errors.owner}>
            <input value={form.owner} onChange={(e) => set({ owner: e.target.value })} />
          </Field>
          <Field label="Entro" error={errors.dueOn}>
            <input
              type="date"
              value={form.dueOn}
              onChange={(e) => set({ dueOn: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Azione" error={errors.action}>
          <textarea
            rows={2}
            value={form.action}
            onChange={(e) => set({ action: e.target.value })}
          />
        </Field>
        <Field label="Stato" error={errors.state}>
          <select
            value={form.state}
            onChange={(e) => set({ state: e.target.value as typeof form.state })}
          >
            {RISK_STATES.map((state) => (
              <option key={state} value={state}>
                {RISK_STATE_LABELS[state]}
              </option>
            ))}
          </select>
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="button" className="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
